/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Multi-format Document Upload Zone & Chunking Configuration (Task 10.3.5)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState, useRef, useCallback } from 'react';
import { useKnowledgeStore, type KnowledgeDocumentItem } from '../../stores/useKnowledgeStore';
import { useI18n } from '../../hooks/useI18n';
import {
  UploadCloud,
  FileText,
  FileCode,
  FileSpreadsheet,
  File,
  X,
  Sparkles,
  CheckCircle2,
  Sliders,
} from 'lucide-react';

interface StagedUploadFile {
  id: string;
  file: File;
  name: string;
  size: number;
  type: 'pdf' | 'docx' | 'txt' | 'csv' | 'markdown';
  status: 'pending' | 'uploading' | 'completed' | 'error';
  progress: number;
}

interface DocumentUploadZoneProps {
  onCompleted?: () => void;
  className?: string;
}

export const DocumentUploadZone: React.FC<DocumentUploadZoneProps> = ({
  onCompleted,
  className = '',
}) => {
  const { t } = useI18n();
  const { addDocuments } = useKnowledgeStore();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [stagedFiles, setStagedFiles] = useState<StagedUploadFile[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  // Chunking Strategy Options
  const [chunkSize, setChunkSize] = useState<number>(512);
  const [chunkOverlap, setChunkOverlap] = useState<number>(50);
  const [embeddingModel, setEmbeddingModel] = useState<string>('@cf/baai/bge-large-en-v1.5');

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const detectFileType = (fileName: string): 'pdf' | 'docx' | 'txt' | 'csv' | 'markdown' => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return 'pdf';
    if (ext === 'docx' || ext === 'doc') return 'docx';
    if (ext === 'csv') return 'csv';
    if (ext === 'md' || ext === 'markdown') return 'markdown';
    return 'txt';
  };

  const getFileIcon = (type: string) => {
    switch (type) {
      case 'pdf':
        return FileText;
      case 'docx':
        return File;
      case 'csv':
        return FileSpreadsheet;
      case 'markdown':
        return FileCode;
      default:
        return FileText;
    }
  };

  const handleFilesSelected = (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const newStaged: StagedUploadFile[] = Array.from(files).map((f) => ({
      id: `file_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
      file: f,
      name: f.name,
      size: f.size,
      type: detectFileType(f.name),
      status: 'pending',
      progress: 0,
    }));

    setStagedFiles((prev) => [...prev, ...newStaged]);
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFilesSelected(e.dataTransfer.files);
    }
  }, []);

  const removeStagedFile = (id: string) => {
    setStagedFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const handleStartIngestion = async () => {
    if (stagedFiles.length === 0 || isProcessing) return;

    setIsProcessing(true);

    const generatedDocs: KnowledgeDocumentItem[] = [];

    // Process files sequentially or parallel
    for (const staged of stagedFiles) {
      // 1. Progress upload simulation
      setStagedFiles((prev) =>
        prev.map((f) => (f.id === staged.id ? { ...f, status: 'uploading', progress: 30 } : f))
      );
      await new Promise((r) => setTimeout(r, 200));

      setStagedFiles((prev) =>
        prev.map((f) => (f.id === staged.id ? { ...f, progress: 75 } : f))
      );
      await new Promise((r) => setTimeout(r, 250));

      setStagedFiles((prev) =>
        prev.map((f) => (f.id === staged.id ? { ...f, status: 'completed', progress: 100 } : f))
      );

      // Estimate chunk count based on file size and chunk size
      const estimatedWords = Math.max(10, Math.floor(staged.size / 6));
      const estimatedChunks = Math.max(1, Math.ceil(estimatedWords / (chunkSize * 0.75)));

      const sampleChunks = [
        {
          id: `chunk_${staged.id}_01`,
          documentId: staged.id,
          chunkIndex: 0,
          tokenCount: Math.min(chunkSize, 450),
          content: `Extracted passage #1 from document "${staged.name}". RAG pipeline automatically chunks text into semantic vector embeddings stored on Cloudflare Vectorize (1536d).`,
          metadata: { page: 1, heading: 'Introductory Section', wordCount: 35 },
        },
      ];

      generatedDocs.push({
        id: staged.id,
        workspaceId: 'ws_demo_primary',
        title: staged.name.replace(/\.[^/.]+$/, ''),
        fileName: staged.name,
        fileType: staged.type,
        fileSizeBytes: staged.size,
        formattedSize: formatFileSize(staged.size),
        status: 'active',
        chunkCount: estimatedChunks,
        embeddingDimensions: 1536,
        embeddingModel,
        r2StorageKey: `rag/ws_demo_primary/${staged.name}`,
        vectorizeIndexName: 'sophia-knowledge-1536',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        sampleChunks,
      });
    }

    addDocuments(generatedDocs);
    setIsProcessing(false);
    setStagedFiles([]);

    if (onCompleted) {
      onCompleted();
    }
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* 1. Drag & Drop Upload Boundary */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`relative flex flex-col items-center justify-center p-8 sm:p-12 rounded-2xl border-2 border-dashed transition-all cursor-pointer ${
          isDragOver
            ? 'border-primary bg-primary/10 scale-[1.01] shadow-glow-primary/20'
            : 'border-surface-border hover:border-primary/50 bg-surface-elevated/30 hover:bg-surface-elevated/60'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.docx,.doc,.txt,.csv,.md"
          className="hidden"
          onChange={(e) => handleFilesSelected(e.target.files)}
        />

        <div className="w-16 h-16 rounded-3xl bg-gradient-to-tr from-primary/20 to-accent/20 border border-primary/30 flex items-center justify-center text-primary shadow-lg mb-4">
          <UploadCloud className="w-8 h-8" />
        </div>

        <div className="text-center space-y-1.5 max-w-md">
          <h3 className="font-heading font-bold text-base text-foreground">
            {t('knowledge.dragDropOrBrowse')}{' '}
            <span className="text-primary hover:underline">{t('knowledge.browseComputer')}</span>
          </h3>
          <p className="text-xs text-foreground-muted leading-relaxed">
            {t('knowledge.supportedFormatsHint')}
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-2 mt-4 text-[11px] text-foreground-muted font-mono">
          <span className="px-2 py-0.5 rounded-md bg-surface border border-surface-border">.pdf</span>
          <span className="px-2 py-0.5 rounded-md bg-surface border border-surface-border">.docx</span>
          <span className="px-2 py-0.5 rounded-md bg-surface border border-surface-border">.txt</span>
          <span className="px-2 py-0.5 rounded-md bg-surface border border-surface-border">.csv</span>
          <span className="px-2 py-0.5 rounded-md bg-surface border border-surface-border">.md</span>
        </div>
      </div>

      {/* 2. Chunking & Vectorize Settings Options */}
      <div className="p-4 rounded-2xl border border-surface-border bg-surface-elevated/40 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sliders className="w-4 h-4 text-primary" />
            <h4 className="font-heading font-bold text-xs text-foreground uppercase tracking-wider">
              {t('knowledge.chunkingConfigTitle')}
            </h4>
          </div>
          <span className="text-[10px] text-accent font-semibold px-2 py-0.5 rounded-full bg-accent/10 border border-accent/20">
            Vectorize 1536d
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {/* Chunk Size */}
          <div className="space-y-1">
            <label className="text-[11px] font-medium text-foreground-muted flex items-center justify-between">
              <span>{t('knowledge.chunkSizeLabel')}</span>
              <span className="font-mono text-primary font-bold">{chunkSize} tokens</span>
            </label>
            <select
              value={chunkSize}
              onChange={(e) => setChunkSize(Number(e.target.value))}
              className="w-full px-2.5 py-1.5 rounded-xl border border-surface-border bg-surface text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value={256}>{t('knowledge.chunkSizeOption256')}</option>
              <option value={512}>{t('knowledge.chunkSizeOption512')}</option>
              <option value={1024}>{t('knowledge.chunkSizeOption1024')}</option>
            </select>
          </div>

          {/* Chunk Overlap */}
          <div className="space-y-1">
            <label className="text-[11px] font-medium text-foreground-muted flex items-center justify-between">
              <span>{t('knowledge.chunkOverlapLabel')}</span>
              <span className="font-mono text-accent font-bold">{chunkOverlap} tokens</span>
            </label>
            <select
              value={chunkOverlap}
              onChange={(e) => setChunkOverlap(Number(e.target.value))}
              className="w-full px-2.5 py-1.5 rounded-xl border border-surface-border bg-surface text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value={20}>{t('knowledge.chunkOverlapOption20')}</option>
              <option value={50}>{t('knowledge.chunkOverlapOption50')}</option>
              <option value={100}>{t('knowledge.chunkOverlapOption100')}</option>
            </select>
          </div>

          {/* Embedding Model */}
          <div className="space-y-1">
            <label className="text-[11px] font-medium text-foreground-muted">
              {t('knowledge.embeddingModelLabel')}
            </label>
            <select
              value={embeddingModel}
              onChange={(e) => setEmbeddingModel(e.target.value)}
              className="w-full px-2.5 py-1.5 rounded-xl border border-surface-border bg-surface text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="@cf/baai/bge-large-en-v1.5">
                {t('knowledge.embeddingWorkersAi')}
              </option>
              <option value="text-embedding-3-small">
                {t('knowledge.embeddingOpenAi')}
              </option>
            </select>
          </div>
        </div>
      </div>

      {/* 3. Staged Files List (Preview & Progress) */}
      {stagedFiles.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between px-1">
            <span className="text-xs font-bold uppercase tracking-wider text-foreground-muted">
              {t('knowledge.stagedFilesTitle', { count: stagedFiles.length })}
            </span>
            <button
              type="button"
              onClick={() => setStagedFiles([])}
              disabled={isProcessing}
              className="text-xs text-destructive hover:underline disabled:opacity-50"
            >
              {t('knowledge.clearAllStaged')}
            </button>
          </div>

          <div className="space-y-2 max-h-56 overflow-y-auto scrollbar-thin">
            {stagedFiles.map((staged) => {
              const FileIconComponent = getFileIcon(staged.type);
              return (
                <div
                  key={staged.id}
                  className="flex items-center justify-between p-3 rounded-xl border border-surface-border bg-surface shadow-sm gap-3"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                      <FileIconComponent className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-foreground truncate max-w-xs sm:max-w-md">
                        {staged.name}
                      </p>
                      <span className="text-[10px] text-foreground-muted">
                        {formatFileSize(staged.size)} • {staged.type.toUpperCase()}
                      </span>
                    </div>
                  </div>

                  {/* Status / Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {staged.status === 'uploading' && (
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-1.5 rounded-full bg-muted overflow-hidden">
                          <div
                            className="h-full bg-primary rounded-full transition-all duration-200"
                            style={{ width: `${staged.progress}%` }}
                          />
                        </div>
                        <span className="text-[10px] font-mono text-primary font-bold">
                          {staged.progress}%
                        </span>
                      </div>
                    )}

                    {staged.status === 'completed' && (
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    )}

                    {!isProcessing && (
                      <button
                        type="button"
                        onClick={() => removeStagedFile(staged.id)}
                        className="p-1 rounded-lg text-foreground-muted hover:text-foreground hover:bg-muted"
                        title={t('knowledge.cancelStagedFile')}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Action Trigger Button */}
          <div className="pt-2">
            <button
              type="button"
              onClick={handleStartIngestion}
              disabled={isProcessing}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-primary text-primary-fg hover:opacity-95 text-xs font-bold shadow-md transition-all active:scale-[0.99] disabled:opacity-50"
            >
              {isProcessing ? (
                <>
                  <div className="w-4 h-4 border-2 border-primary-fg border-t-transparent rounded-full animate-spin" />
                  <span>{t('knowledge.ingestingPipelineRunning')}</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  <span>
                    {t('knowledge.startIngestionWithCount', { count: stagedFiles.length })}
                  </span>
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
